import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Link,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  FormControlLabel,
  Switch,
  Stack,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { auth } from '../services/api';

interface RegisterFormData {
  email: string;
  lastName: string;
  firstName: string;
  middleName: string;
  password: string;
  confirmPassword: string;
  organization: string;
  position: string;
  role: 'author'; // Только авторы
}

const Register: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<RegisterFormData>({
    email: '',
    lastName: '',
    firstName: '',
    middleName: '',
    password: '',
    confirmPassword: '',
    organization: '',
    position: '',
    role: 'author', // Только авторы
  });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Убираем переключение ролей - только авторы
  // const handleRoleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const role = e.target.checked ? 'author' : 'user';
  //   setFormData((prev) => ({
  //     ...prev,
  //     role,
  //     // Очищаем поля организации и должности при смене роли
  //     ...(role === 'user' ? { organization: '', position: '' } : {}),
  //   }));
  // };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (formData.password !== formData.confirmPassword) {
        setError('Пароли не совпадают');
        return;
      }

      const registerData = {
        email: formData.email,
        password: formData.password,
        lastName: formData.lastName,
        firstName: formData.firstName,
        middleName: formData.middleName || undefined,
        role: 'author' as const, // Только авторы
        organization: formData.organization,
        position: formData.position,
      };

      const response = await auth.register(registerData);

      // Если требуется подтверждение, перенаправляем на страницу подтверждения
      if (response.data.requiresConfirmation) {
        navigate(`/confirm-registration?email=${encodeURIComponent(formData.email)}`);
      } else {
        // Сохраняем токены и перенаправляем на главную страницу
      sessionStorage.setItem('token', response.data.token);
      sessionStorage.setItem('refreshToken', response.data.refreshToken);
      navigate('/');
      }
    } catch (error: any) {
      console.error('Ошибка при регистрации:', error);
      setError(error.response?.data?.message || 'Ошибка при регистрации');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Регистрация автора
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6" align="center" color="primary" gutterBottom>
                Регистрация автора
              </Typography>
              <Typography variant="body2" align="center" color="text.secondary" sx={{ mb: 2 }}>
                Зарегистрируйтесь как автор для публикации статей в журнале
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                required
                fullWidth
                id="lastName"
                label="Фамилия"
                name="lastName"
                autoComplete="family-name"
                value={formData.lastName}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                required
                fullWidth
                id="firstName"
                label="Имя"
                name="firstName"
                autoComplete="given-name"
                value={formData.firstName}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                id="middleName"
                label="Отчество"
                name="middleName"
                autoComplete="additional-name"
                value={formData.middleName}
                onChange={handleChange}
                disabled={loading}
                helperText="Необязательно"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                id="email"
                label="Email"
                name="email"
                type="email"
                autoComplete="email"
                value={formData.email}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                id="organization"
                label="Организация"
                name="organization"
                value={formData.organization}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                id="position"
                label="Должность"
                name="position"
                value={formData.position}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                name="password"
                label="Пароль"
                type="password"
                id="password"
                autoComplete="new-password"
                value={formData.password}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                name="confirmPassword"
                label="Подтвердите пароль"
                type="password"
                id="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                disabled={loading}
              />
            </Grid>
          </Grid>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading}
          >
            {loading ? 'Регистрация...' : 'Зарегистрироваться как автор'}
          </Button>
          <Box sx={{ textAlign: 'center' }}>
            <Link component={RouterLink} to="/login" variant="body2">
              Уже есть аккаунт? Войдите
            </Link>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default Register; 